import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { suggestedLanguages, allLanguages, getLanguageByCode } from '../data/languages';

// Language code map for HTML lang attribute (matches API MultiLanguage table)
const languageCodeMap = {
  'EN': 'en',
  'TR': 'tr',
  'AR': 'ar',
  'RU': 'ru',
  'ES': 'es',
  'BG': 'bg',
  'CS': 'cs',
  'DA': 'da',
  'DE': 'de',
  'EL': 'el',
  'ET': 'et',
  'FI': 'fi',
  'FR': 'fr',
  'HE': 'he',
  'HU': 'hu',
  'ID': 'id',
  'IT': 'it',
  'JA': 'ja',
  'KO': 'ko',
  'LT': 'lt',
  'LV': 'lv',
  'NB': 'nb',
  'NL': 'nl',
  'PL': 'pl',
  'PT': 'pt',
  'RO': 'ro',
  'SK': 'sk',
  'SL': 'sl',
  'SV': 'sv',
  'TH': 'th',
  'UK': 'uk',
  'VI': 'vi',
  'ZH': 'zh',
};

const LanguageSelector = ({ 
  currentLanguage, 
  onLanguageChange,
  buttonClassName = "",
  showButton = true
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const modalRef = useRef(null);
  const buttonRef = useRef(null);
  const isMountedRef = useRef(true);

  // Ensure component is mounted before rendering portal
  useEffect(() => {
    setMounted(true);
    isMountedRef.current = true;
    return () => {
      // Close modal before unmounting to prevent portal errors
      isMountedRef.current = false;
      setIsModalOpen(false);
      setMounted(false);
    };
  }, []);

  // Use all languages directly (no filtering)
  const filteredSuggested = suggestedLanguages;
  const filteredAll = allLanguages;

  // Close modal when clicking outside
  useEffect(() => {
    if (!isModalOpen || !mounted) return;

    const handleClickOutside = (event) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsModalOpen(false);
      }
    };

    // Check if document.body exists before adding listeners
    if (typeof document !== 'undefined' && document.body) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }

    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('mousedown', handleClickOutside);
        if (document.body) {
          document.body.style.overflow = 'unset';
        }
      }
    };
  }, [isModalOpen, mounted]);

  // Close modal on Escape key
  useEffect(() => {
    if (!isModalOpen || !mounted) return;

    const handleEscape = (event) => {
      if (event.key === 'Escape' && isModalOpen) {
        setIsModalOpen(false);
      }
    };

    if (typeof document !== 'undefined') {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isModalOpen, mounted]);

  // Close modal on viewport/resize changes (when switching desktop/mobile)
  useEffect(() => {
    if (!isModalOpen || !mounted) return;

    let resizeTimer;
    const handleResize = () => {
      // Close modal when viewport changes significantly (desktop to mobile or vice versa)
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (isMountedRef.current) {
          setIsModalOpen(false);
        }
      }, 100);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
        clearTimeout(resizeTimer);
      };
    }
  }, [isModalOpen, mounted]);

  // Apply RTL on mount if saved
  useEffect(() => {
    const savedRTL = localStorage.getItem('isRTL');
    if (savedRTL === 'true') {
      document.documentElement.setAttribute('dir', 'rtl');
    } else {
      document.documentElement.setAttribute('dir', 'ltr');
    }
  }, []);

  const handleLanguageSelect = (language) => {
    // Update language via callback (this updates localStorage and TranslationContext)
    onLanguageChange(language);
    setIsModalOpen(false);

    // Save multilanguageid to localStorage for API calls
    localStorage.setItem('multilanguageid', (language.id || 1).toString());

    // Apply RTL for Arabic and Hebrew, LTR for others
    const rtlLanguages = ['AR', 'HE', 'FA', 'UR']; // Arabic, Hebrew, Persian, Urdu
    const isRTL = rtlLanguages.includes(language.code);

    // Apply direction to html element
    if (isRTL) {
      document.documentElement.setAttribute('dir', 'rtl');
      document.documentElement.setAttribute('lang', languageCodeMap[language.code] || 'ar');
    } else {
      document.documentElement.setAttribute('dir', 'ltr');
      document.documentElement.setAttribute('lang', languageCodeMap[language.code] || 'en');
    }

    // Save RTL state to localStorage
    localStorage.setItem('isRTL', isRTL.toString());
  };

  const currentLang = currentLanguage || getLanguageByCode('EN');

  return (
    <>
      {showButton && (
        <button
          ref={buttonRef}
          onClick={() => setIsModalOpen(true)}
          className={buttonClassName || "flex items-center justify-center"}
        >
          {currentLang.flag && currentLang.flag.startsWith('http') ? (
            <img 
              src={currentLang.flag} 
              alt={currentLang.name} 
              className="w-7 h-7 object-cover rounded-full shadow-sm border border-gray-200"
              style={{ imageRendering: 'crisp-edges' }}
              loading="eager"
            />
          ) : (
            <span className="text-base">{currentLang.flagEmoji || currentLang.flag}</span>
          )}
          {/* <span>{currentLang.code}</span> */}
          {/* <i className="fas fa-chevron-down text-xs"></i> */}
        </button>
      )}

      {/* Modal Overlay - Rendered via Portal at root level to avoid z-index issues */}
      {isModalOpen && mounted && isMountedRef.current && typeof document !== 'undefined' && document.body && document.body.parentNode && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" 
          style={{ 
            zIndex: 10000,
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsModalOpen(false);
            }
          }}
        >
          <div
            ref={modalRef}
            className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col animate-fadeIn relative notranslate"
            style={{ zIndex: 10001, direction: 'ltr' }}
            onClick={(e) => e.stopPropagation()}
            translate="no"
            dir="ltr"
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 notranslate" translate="no">
              <h2 className="text-lg font-semibold text-gray-900 notranslate" translate="no">
                Select your language
              </h2>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 hover:bg-gray-100 rounded-full"
                aria-label="Close"
              >
                <i className="fas fa-times text-lg"></i>
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Suggested Languages Section */}
              {filteredSuggested.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 notranslate" translate="no">
                    Suggested for you
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {filteredSuggested.map((language) => (
                      <button
                        key={language.code}
                        onClick={() => handleLanguageSelect(language)}
                        className={`flex items-center gap-2 p-3 rounded-lg transition-colors ${
                          currentLang.code === language.code
                            ? 'bg-blue-50'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-2 flex-1 notranslate" translate="no">
                          {language.flag && language.flag.startsWith('http') ? (
                            <img
                              src={language.flag}
                              alt={language.name}
                              className="w-7 h-5 object-cover rounded"
                              style={{ imageRendering: 'crisp-edges' }}
                              loading="eager"
                            />
                          ) : (
                            <span className="text-lg">{language.flagEmoji || language.flag}</span>
                          )}
                          <div className="flex flex-col items-start">
                            <span className={`text-sm font-medium ${currentLang.code === language.code ? 'text-blue-700' : 'text-gray-900'}`}>
                              {language.name}
                            </span>
                            <span className={`text-xs ${currentLang.code === language.code ? 'text-blue-500' : 'text-gray-400'}`}>
                              {language.code}
                            </span>
                          </div>
                        </div>
                        {currentLang.code === language.code && (
                          <i className="fas fa-check text-blue-500 text-sm"></i>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* All Languages Section */}
              {filteredAll.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 notranslate" translate="no">
                    All languages
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {filteredAll.map((language) => (
                      <button
                        key={language.code}
                        onClick={() => handleLanguageSelect(language)}
                        className={`flex items-center gap-2 p-3 rounded-lg transition-colors ${
                          currentLang.code === language.code
                            ? 'bg-blue-50'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-2 flex-1 notranslate" translate="no">
                          {language.flag && language.flag.startsWith('http') ? (
                            <img
                              src={language.flag}
                              alt={language.name}
                              className="w-7 h-5 object-cover rounded"
                              style={{ imageRendering: 'crisp-edges' }}
                              loading="eager"
                            />
                          ) : (
                            <span className="text-lg">{language.flagEmoji || language.flag}</span>
                          )}
                          <div className="flex flex-col items-start">
                            <span className={`text-sm font-medium ${currentLang.code === language.code ? 'text-blue-700' : 'text-gray-900'}`}>
                              {language.name}
                            </span>
                            <span className={`text-xs ${currentLang.code === language.code ? 'text-blue-500' : 'text-gray-400'}`}>
                              {language.code}
                            </span>
                          </div>
                        </div>
                        {currentLang.code === language.code && (
                          <i className="fas fa-check text-blue-500 text-sm"></i>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Add fadeIn animation if not in Tailwind config */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
      `}</style>
    </>
  );
};

export default LanguageSelector;

